import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ease } from "@/lib/motion";
import { useUIStore } from "@/stores/ui";
import { useThemeStore } from "@/stores/theme";
import { useAuthStore } from "@/stores/auth";
import { useAiAssistantEnabled } from "@/features/settings/hooks/useSettings";
import InputBase from "@mui/material/InputBase";
import IconButton from "@/components/common/GoogleIconButton";
import { CommandItem } from "./CommandPalette/CommandItem";
import { buildAllItems } from "./CommandPalette/items";
import {
  getRecentPaths,
  addRecentPath,
  CATEGORY_ORDER,
} from "./CommandPalette/utils";
import type { CmdItem } from "./CommandPalette/types";
import { useGoogleText } from "@/lib/translation/useGoogleText";

const PAGE_PATHS: Record<string, string> = {
  "/home": "p-home",
  "/dashboard": "p-dash",
  "/reports": "p-reports",
  "/log/emissions": "p-emissions",
  "/targets": "p-targets",
  "/assets": "p-assets",
  "/offsets": "p-offsets",
  "/organisation/facilities": "p-facilities",
  "/organisation/employees": "p-employees",
  "/organisation/disclosures": "p-disclosures",
  "/audits": "p-audits",
  "/datasource/standardemissionfactor": "p-std-factors",
  "/datasource/customemissionfactor": "p-cust-factors",
  "/partners/suppliers/dashboard": "p-suppliers",
  "/partners/customers": "p-customers",
  "/cat-map": "p-catmap",
  "/bulk-upload": "p-bulk",
  "/resourcecentre": "p-resource",
  "/chat": "p-chat",
};

export function CommandPalette() {
  const isOpen = useUIStore((s) => s.commandPaletteOpen);
  const setOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const logout = useAuthStore((s) => s.logout);
  const role = useAuthStore((s) => s.user?.role);
  const toggleAIPanel = useUIStore((s) => s.toggleAIPanel);
  const aiEnabled = useAiAssistantEnabled();
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [prevOpen, setPrevOpen] = useState(isOpen);
  if (prevOpen !== isOpen) {
    setPrevOpen(isOpen);
    if (isOpen) setSearch("");
  }

  const go = useCallback(
    (path: string) => {
      navigate(path);
      setOpen(false);
    },
    [navigate, setOpen],
  );

  const allItems = useMemo(
    () =>
      buildAllItems({
        go,
        theme,
        setTheme,
        toggleAIPanel,
        setOpen,
        logout,
        aiEnabled,
        role,
      }),
    [theme, setTheme, toggleAIPanel, setOpen, logout, go, aiEnabled, role],
  );

  const recentItems = useMemo(() => {
    const paths = getRecentPaths();
    return paths
      .map((path) => {
        const id = PAGE_PATHS[path];
        return id ? allItems.find((item) => item.id === id) : undefined;
      })
      .filter(Boolean)
      .slice(0, 3) as CmdItem[];
  }, [allItems]);

  const translatedText = useGoogleText(
    allItems.flatMap((item) => [item.label, item.category, ...item.keywords]),
  );

  const matchedItems = useMemo(() => {
    if (!search) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.keywords.some((kw) => kw.includes(q)) ||
        item.category.toLowerCase().includes(q) ||
        translatedText(item.label).toLowerCase().includes(q) ||
        translatedText(item.category).toLowerCase().includes(q) ||
        item.keywords.some((kw) =>
          translatedText(kw).toLowerCase().includes(q),
        ),
    );
  }, [allItems, search, translatedText]);

  const grouped = useMemo(() => {
    const groups: Record<string, CmdItem[]> = {};
    for (const item of matchedItems) {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    }
    const ordered: [string, CmdItem[]][] = [];
    for (const cat of CATEGORY_ORDER) {
      if (groups[cat]) ordered.push([cat, groups[cat]]);
    }
    for (const [cat, items] of Object.entries(groups)) {
      if (!CATEGORY_ORDER.includes(cat as (typeof CATEGORY_ORDER)[number])) {
        ordered.push([cat, items]);
      }
    }
    return ordered;
  }, [matchedItems]);

  const flatItems = useMemo(() => {
    const list: CmdItem[] = [];
    if (!search && recentItems.length > 0) list.push(...recentItems);
    for (const [, items] of grouped) list.push(...items);
    return list;
  }, [search, recentItems, grouped]);

  const [prevSearch, setPrevSearch] = useState(search);
  if (prevSearch !== search) {
    setPrevSearch(search);
    setActiveId(flatItems[0]?.id ?? null);
  } else if (!flatItems.some((item) => item.id === activeId)) {
    // Google's results can arrive after the query, without another keystroke.
    // Preserve a still-visible selection, otherwise select the first match.
    const nextId = flatItems[0]?.id ?? null;
    if (activeId !== nextId) setActiveId(nextId);
  }

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    addRecentPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(!isOpen);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, setOpen]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-id="${activeId}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  const moveActive = useCallback(
    (dir: 1 | -1) => {
      if (flatItems.length === 0) return;
      const idx = flatItems.findIndex((i) => i.id === activeId);
      const next = Math.min(
        Math.max((idx < 0 ? 0 : idx) + dir, 0),
        flatItems.length - 1,
      );
      setActiveId(flatItems[next].id);
    },
    [flatItems, activeId],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        moveActive(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        moveActive(-1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flatItems.find((i) => i.id === activeId);
        item?.onSelect();
      }
    },
    [flatItems, activeId, moveActive, setOpen],
  );

  const renderGroup = (title: string, items: CmdItem[]) => (
    <div key={title} className="mb-1">
      <div className="px-3 pt-3 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/50">
        {title}
      </div>
      {items.map((item) => (
        <CommandItem
          key={item.id}
          item={item}
          active={item.id === activeId}
          onHover={() => setActiveId(item.id)}
        />
      ))}
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50"
            style={{
              background: "var(--backdrop)",
              backdropFilter: "blur(12px) saturate(120%)",
              WebkitBackdropFilter: "blur(12px) saturate(120%)",
            }}
            onClick={() => setOpen(false)}
          />

          <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={ease}
              className="w-full max-w-xl pointer-events-auto"
            >
              <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-primary/20 via-transparent to-primary/10 shadow-[0_0_60px_-12px_var(--primary-20)]">
                <div className="rounded-2xl border border-border/60 bg-card shadow-overlay overflow-hidden">
                  {/* Search */}
                  <div className="relative flex items-center gap-2.5 px-4 h-12 border-b border-border/40">
                    <Search
                      size={15}
                      className="text-muted-foreground/50 shrink-0"
                    />
                    <InputBase
                      inputRef={inputRef}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Search pages, actions, settings..."
                      fullWidth
                      sx={{
                        flex: 1,
                        height: "100%",
                        fontSize: "0.875rem",
                        color: "var(--foreground)",
                        "& .MuiInputBase-input": {
                          padding: "0.375rem 0",
                          borderRadius: 0,
                          border: "none",
                          outline: "none",
                          boxShadow: "none",
                          "&:focus": { outline: "none", boxShadow: "none" },
                          "&:focus-visible": {
                            outline: "none",
                            boxShadow: "none",
                          },
                          "&::placeholder": {
                            color: "var(--muted-foreground-40)",
                          },
                        },
                      }}
                    />
                    {search ? (
                      <IconButton
                        type="button"
                        onClick={() => setSearch("")}
                        aria-label="Clear search"
                        size="small"
                        sx={{
                          padding: "0.25rem",
                          borderRadius: "0.5rem",
                          color: "var(--muted-foreground-40)",
                          "&:hover": {
                            color: "var(--foreground)",
                            backgroundColor: "var(--muted-50)",
                          },
                        }}
                      >
                        <X size={13} />
                      </IconButton>
                    ) : (
                      <kbd className="hidden sm:inline-flex items-center rounded-md border border-border/50 bg-muted/30 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground/40">
                        ESC
                      </kbd>
                    )}
                  </div>

                  {/* Results */}
                  <div
                    ref={listRef}
                    className="max-h-[360px] overflow-y-auto p-2"
                  >
                    {flatItems.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/40 mb-3">
                          <Search
                            size={18}
                            className="text-muted-foreground/40"
                          />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground/70">
                          No results found
                        </p>
                        <p className="text-xs text-muted-foreground/40 mt-1">
                          Try a different search term
                        </p>
                      </div>
                    ) : (
                      <>
                        {!search &&
                          recentItems.length > 0 &&
                          renderGroup("Recent", recentItems)}
                        {grouped.map(([category, items]) =>
                          renderGroup(category, items),
                        )}
                      </>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between px-4 py-2 border-t border-border/30 bg-muted/20">
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground/50">
                      <span className="flex items-center gap-1">
                        <kbd className="inline-flex items-center justify-center rounded border border-border/50 bg-muted/30 w-4 h-4 text-[9px]">
                          ↑
                        </kbd>
                        <kbd className="inline-flex items-center justify-center rounded border border-border/50 bg-muted/30 w-4 h-4 text-[9px]">
                          ↓
                        </kbd>
                        <span className="ml-0.5">Navigate</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="inline-flex items-center justify-center rounded border border-border/50 bg-muted/30 w-4 h-4 text-[9px]">
                          ↵
                        </kbd>
                        <span className="ml-0.5">Select</span>
                      </span>
                    </div>
                    <span className="text-[10px] font-medium text-muted-foreground/30 tracking-wide uppercase">
                      PlanetWise
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
